/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type SoapInput } from '@therapy/shared-validators';
import { Lock } from 'lucide-react';
import { type SyntheticEvent, type JSX } from 'react';
import { type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type PlanGoal } from '../../lib/api/itp.js';

import { SoapFields } from './soap-fields.js';
import { type TrialCounts, type TrialField, TrialsEditor } from './trials-editor.js';

interface NoteFormProps {
  readonly register: UseFormRegister<SoapInput>;
  readonly errors: FieldErrors<SoapInput>;
  readonly goals: readonly PlanGoal[];
  readonly trials: Readonly<Record<string, TrialCounts>>;
  readonly onTrialChange: (goalId: string, field: TrialField, value: string) => void;
  readonly locked: boolean;
  readonly saving: boolean;
  readonly signing: boolean;
  readonly canSign: boolean;
  readonly saveError: boolean;
  readonly onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
  readonly onSign: () => void;
}

/** The SOAP + trials form body; the page owns the data + handlers. */
export function NoteForm({
  register,
  errors,
  goals,
  trials,
  onTrialChange,
  locked,
  saving,
  signing,
  canSign,
  saveError,
  onSubmit,
  onSign,
}: NoteFormProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="space-y-6" noValidate>
      <SoapFields register={register} errors={errors} disabled={locked} />

      {goals.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold text-slate-700">
            {t('therapist.note.trials')}
          </h2>
          <TrialsEditor goals={goals} values={trials} onChange={onTrialChange} disabled={locked} />
        </section>
      )}

      {saveError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      {!locked && (
        <div className="flex justify-end gap-3">
          <Button type="submit" variant="outline" disabled={saving}>
            {t('therapist.note.saveDraft')}
          </Button>
          <Button type="button" onClick={onSign} disabled={!canSign || signing}>
            <Lock className="h-4 w-4" aria-hidden="true" />
            {t('therapist.note.sign')}
          </Button>
        </div>
      )}
    </form>
  );
}
